import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';

import {
  ref as storageRef,
  uploadBytes
} from 'firebase/storage';

import { db, storage } from './fire-init';
import { environment } from '../../environments/environment';
import { LibraryItem, LibraryType } from 'src/typings/library.interface';

@Injectable({ providedIn: 'root' })
export class TennisVideoService {
  private readonly itemsSubject = new BehaviorSubject<LibraryItem[]>([]);
  readonly items$ = this.itemsSubject.asObservable();

  // You can keep 'media' or change to 'tennisMedia' if you want a dedicated collection
  private readonly collectionPath = 'media';

  // Buckets from environment
  private readonly uploadBucketName = environment.uploadBucketName;
  private readonly outputBucketName =
    environment.outputBucketName || environment.uploadBucketName;

  // Base URLs derived from buckets
  private get publicUploadBase(): string {
    return `https://storage.googleapis.com/${this.uploadBucketName}`;
  }

  private get publicOutputBase(): string {
    return `https://storage.googleapis.com/${this.outputBucketName}`;
  }

  constructor() {
    this.listenToMediaCollection();
  }

  // ---------------------------------------------------------------------------
  // Anonymous / “user” ID (no Auth)
  // ---------------------------------------------------------------------------

  /**
   * Returns a stable anonymous uid per browser using localStorage.
   * If you later add Firebase Auth, you can swap this out easily.
   */
  private getClientUid(): string {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return 'anonymous';
      }
      const key = 'tennis_video_anonymous_uid';
      let uid = localStorage.getItem(key);
      if (!uid) {
        uid = uuidv4();
        localStorage.setItem(key, uid);
      }
      return uid;
    } catch {
      // If localStorage is unavailable, fall back to a generic id.
      return 'anonymous';
    }
  }

  // ---------------------------------------------------------------------------
  // Firestore listener
  // ---------------------------------------------------------------------------

  private listenToMediaCollection(): void {
    const colRef = collection(db, this.collectionPath);
    // Pinned first, then custom order, then newest updated
    const qRef = query(
      colRef,
      orderBy('pinned', 'desc'),
      orderBy('orderIndex', 'asc'),
      orderBy('updatedAt', 'desc')
    );

    onSnapshot(qRef, snapshot => {
      const items: LibraryItem[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data() as any;
        const item: LibraryItem = {
          id: docSnap.id,
          uid: data.uid,
          title: data.title || '',
          description: data.description || '',
          labels: data.labels || [],
          categories: data.categories || [],
          color: data.color || '#ffffff',
          pinned: !!data.pinned,
          archived: !!data.archived,
          libraryType: (data.libraryType as LibraryType) || 'unknown',
          source: data.source,
          mediaUrl: data.mediaUrl,
          thumbnailUrl: data.thumbnailUrl,
          status: data.status,
          transcript: data.transcript,
          sentimentScore: data.sentimentScore,
          sentimentMagnitude: data.sentimentMagnitude,
          analysisJsonUri: data.analysisJsonUri,
          // pose now holds our tennis serve analysis (trophy angle, samples, category)
          pose: data.pose || null,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          orderIndex: data.orderIndex
        };

        items.push(item);
      });

      this.itemsSubject.next(items);
    });
  }

  // ---------------------------------------------------------------------------
  // Creation / upload
  // ---------------------------------------------------------------------------

  async createItemWithUpload(
    payload: {
      title: string;
      description: string;
      labels: string[];
      color: string;
      pinned: boolean;
    },
    file: File
  ): Promise<void> {
    const uid = this.getClientUid();

    const mediaId = uuidv4();
    const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
    const objectPath = `uploads/${uid}/${mediaId}.${ext}`;
    const contentType = file.type || 'application/octet-stream';

    // Derive mediaType from mimetype
    const mediaType: LibraryType = contentType.startsWith('video/')
      ? 'video'
      : contentType.startsWith('image/')
      ? 'image'
      : contentType.startsWith('audio/')
      ? 'audio'
      : 'unknown';

    const now = new Date().toISOString();

    // Pre-create Firestore doc so backend can update it
    const docRef = doc(db, this.collectionPath, mediaId);
    const bucketName = this.uploadBucketName;

    const mediaUrl = `${this.publicUploadBase}/${objectPath}`;
    const sourceGcsUri = `gs://${bucketName}/${objectPath}`;

    const initialDoc = {
      uid,
      title: payload.title,
      description: payload.description,
      labels: payload.labels || [],
      categories: [] as string[],
      color: payload.color,
      pinned: payload.pinned,
      archived: false,
      mediaType,
      source: sourceGcsUri,
      mediaUrl,
      // thumbnailUrl + pose + analysisJsonUri will be added by the backend
      status: 'uploading',
      createdAt: now,
      updatedAt: now,
      orderIndex: Date.now()
    };

    await setDoc(docRef, initialDoc);

    // Upload file to Storage
    const sRef = storageRef(storage, objectPath);
    await uploadBytes(sRef, file, { contentType });

    // After upload:
    //   - GCS finalize -> GCF (index.js / upload handler)
    //   - Pub/Sub -> tennis worker (main.py)
    //   - Worker writes analysis + thumbnail and updates Firestore doc.
  }

  // ---------------------------------------------------------------------------
  // Updates used by VideoLibraryComponent
  // ---------------------------------------------------------------------------

  async updateItem(id: string, patch: Partial<LibraryItem>): Promise<void> {
    const docRef = doc(db, this.collectionPath, id);
    const { id: _omitId, uid, ...rest } = patch as any;
    await updateDoc(docRef, {
      ...rest,
      updatedAt: new Date().toISOString()
    });
  }

  async deleteItem(id: string): Promise<void> {
    const docRef = doc(db, this.collectionPath, id);
    await deleteDoc(docRef);
    // Optional: also delete from Storage (storageRef + deleteObject) if desired.
  }

  async reorderItems(orderedIds: string[]): Promise<void> {
    const batch = writeBatch(db);
    orderedIds.forEach((id, index) => {
      const docRef = doc(db, this.collectionPath, id);
      batch.update(docRef, {
        orderIndex: index,
        updatedAt: new Date().toISOString()
      });
    });
    await batch.commit();
  }

  async triggerAnalysis(id: string): Promise<void> {
    const docRef = doc(db, this.collectionPath, id);
    await updateDoc(docRef, {
      status: 'processing',
      reanalyzeRequestedAt: new Date().toISOString()
    });
  }
}
